const NETBALL_CONNECT_DRAW_RESULT_ID = 3
const PERIOD_SCORE_ATTEMPT_CACHE_KEY = 'fixturePeriodScoreAttempts'
const QUICK_PERIOD_SCORE_RECENT_WINDOW_MS = 14 * 24 * 60 * 60 * 1000
const QUICK_PERIOD_SCORE_RETRY_MS = 6 * 60 * 60 * 1000
const PERIOD_SCORE_ATTEMPT_CACHE_MAX_ENTRIES = 1500

export type FixtureSyncGame = {
	id: ConnectorMatchId
	date: number
	opponent: string
	round: string
	venue: string
	myGameId?: GameId
	result?: Result
}

type FixtureOrderValue = {
	date: number
	round: string
	key: string | number
}

export function compareFixtureOrder(a: FixtureOrderValue, b: FixtureOrderValue) {
	const dateA = Number.isFinite(a.date) ? a.date : Number.MAX_SAFE_INTEGER
	const dateB = Number.isFinite(b.date) ? b.date : Number.MAX_SAFE_INTEGER
	return dateA - dateB ||
		String(a.round).localeCompare(String(b.round), undefined, { numeric: true, sensitivity: 'base' }) ||
		String(a.key).localeCompare(String(b.key))
}

function queryChangeOrderValue(change: Changes[number], games: Games | null | undefined): FixtureOrderValue {
	if ('add' in change || 'delete' in change) {
		return {
			date: change.game.date,
			round: change.game.round,
			key: 'add' in change ? change.matchId ?? `${change.game.date}-${change.game.opponent}` : change.delete,
		}
	}

	const game = games?.[change.myGameId]
	const changedDate = change.changes.find(field => field.field === 'date')?.newValue
	const changedRound = change.changes.find(field => field.field === 'round')?.newValue
	return {
		date: typeof changedDate === 'number' ? changedDate : game?.date ?? Number.MAX_SAFE_INTEGER,
		round: typeof changedRound === 'string' ? changedRound : game?.round ?? '',
		key: change.myGameId,
	}
}

export function sortFixtureChanges(changes: Changes, games?: Games | null): Changes {
	return [...changes].sort((a, b) => {
		const deleteOrder = Number('delete' in a) - Number('delete' in b)
		return deleteOrder || compareFixtureOrder(queryChangeOrderValue(a, games), queryChangeOrderValue(b, games))
	})
}

type PeriodScoreAttemptCache = Record<string, {
	checkedAt: number
	scoreKey: string
	hadPeriodScores: boolean
}>

type PeriodScoreAttemptProps = {
	cacheKey: string
	mode: FixtureSyncMode
	gameDate: number
	localGameHasDetailedScores: boolean
	status?: string | null
	meScore?: number | null
	themScore?: number | null
}

// Build a normal Result when NetballConnect only gives us a trustworthy final
// score. This keeps downstream score display/editing code using the same shape
// as period-by-period results while leaving earlier periods intentionally blank.
export function makeFinalOnlyResult(meScore: number, themScore: number, numPeriods: number): Result {
	const result: Result = {
		me: new Array(numPeriods).fill(null),
		them: new Array(numPeriods).fill(null),
	}

	if (numPeriods > 0) {
		result.me[numPeriods - 1] = meScore
		result.them[numPeriods - 1] = themScore
	}

	return result
}

type NetballConnectMatch = NetballConnectMatches['rounds'][number]['matches'][number]

type NetballConnectMatchCompletionSignals = {
	endTime?: NetballConnectMatch['endTime'] | null
	matchEnded?: NetballConnectMatch['matchEnded'] | null
	matchStatus?: NetballConnectMatch['matchStatus'] | null
	team1ResultId?: NetballConnectMatch['team1ResultId'] | null
	team2ResultId?: NetballConnectMatch['team2ResultId'] | null
}

type NetballConnectHeadlineScoreSignals = NetballConnectMatchCompletionSignals & {
	meScore?: number | null
	themScore?: number | null
}

// NetballConnect can expose contradictory completion fields; in observed data,
// completed games often have matchEnded=false. This helper keeps the fallback
// gate centralized so we require multiple signals before trusting headline
// scores when periodScores are missing or unusable.
export function isSafeToFallbackToFinalScore(match: NetballConnectMatchCompletionSignals) {
	const strongSignals = [
		!!match.endTime,
		!!match.matchEnded,
		String(match.matchStatus || '').toUpperCase() === 'ENDED',
		match.team1ResultId != null && match.team2ResultId != null,
	]

	// NetballConnect can mark a match as FINAL before it is really settled.
	// Require multiple completion signals before we trust a periodScores miss
	// enough to synthesize a final-only result.
	return strongSignals.filter(Boolean).length >= 2
}

// Decide whether the headline score is safe enough to synthesize a final-only
// result. The main traps are overwriting existing detailed local scores and
// treating NetballConnect's ambiguous 0-0 completed games as real draws when
// they may be forfeits, abandonments, or simply missing score data.
export function canUseHeadlineFinalScore(
	match: NetballConnectHeadlineScoreSignals,
	hasExistingDetailedScores: boolean,
): match is NetballConnectHeadlineScoreSignals & { meScore: number; themScore: number } {
	if (hasExistingDetailedScores) {
		return false
	}

	if (
		typeof match.meScore !== 'number' ||
		typeof match.themScore !== 'number' ||
		!Number.isFinite(match.meScore) ||
		!Number.isFinite(match.themScore)
	) {
		return false
	}

	if (!isSafeToFallbackToFinalScore(match)) {
		return false
	}

	const isNilAllDraw = match.meScore === 0 && match.themScore === 0
	if (isNilAllDraw) {
		return match.team1ResultId === NETBALL_CONNECT_DRAW_RESULT_ID &&
			match.team2ResultId === NETBALL_CONNECT_DRAW_RESULT_ID
	}

	return true
}

export function shouldFetchPeriodScores(props: PeriodScoreAttemptProps) {
	if (props.gameDate > Date.now()) {
		return false
	}

	if (props.localGameHasDetailedScores) {
		return false
	}

	if (props.mode === 'comprehensive') {
		return true
	}

	if (!hasFiniteScore(props.meScore) || !hasFiniteScore(props.themScore)) {
		return false
	}

	if (!isRecentlyCompleted(props.gameDate)) {
		return false
	}

	const cacheEntry = loadPeriodScoreAttemptCache()[props.cacheKey]
	if (
		cacheEntry &&
		cacheEntry.scoreKey === periodScoreAttemptScoreKey(props) &&
		!cacheEntry.hadPeriodScores &&
		Date.now() - cacheEntry.checkedAt < QUICK_PERIOD_SCORE_RETRY_MS
	) {
		return false
	}

	return true
}

export function rememberPeriodScoreFetchAttempt(props: PeriodScoreAttemptProps, hadPeriodScores: boolean) {
	const cache = loadPeriodScoreAttemptCache()
	cache[props.cacheKey] = {
		checkedAt: Date.now(),
		scoreKey: periodScoreAttemptScoreKey(props),
		hadPeriodScores,
	}
	savePeriodScoreAttemptCache(cache)
}

// Runs the standard two-pass round/date/fuzzy matching between fixture entries and local games.
// Pass 1: exact round name or exact date. Pass 2: also fuzzy date (within 4 hours).
// Returns a Map of localGameId -> fixtureMatchId.
export function pairFixtureGames(
	fixtures: Array<{ id: ConnectorMatchId; date: number; round: string }>,
	localGames: Games,
	skip: (game: Game) => boolean = () => false,
): Map<string, ConnectorMatchId> {
	const claimed: { [key: string]: boolean } = {}
	const result = new Map<string, ConnectorMatchId>()
	;([1, 2] as const).forEach(pass => {
		fixtures.forEach(match => {
			const gameId = Object.keys(localGames).find(gid => {
				if (claimed[gid]) return false
				const game = localGames[gid]
				if (skip(game)) return false
				if (isSameRound(match.round, game.round)) return true
				if (match.date === game.date) return true
				if (pass > 1 && Math.abs(match.date - game.date) < 4 * 3600 * 1000) return true
				return false
			})
			if (gameId && !(gameId in claimed)) {
				claimed[gameId] = true
				result.set(gameId, match.id)
			}
		})
	})
	return result
}

export function buildFixtureSyncResult(
	fixtures: FixtureSyncGame[],
	games: Games,
	skipMatchIds: ConnectorMatchId[] = [],
): SyncResult {
	const workingGames: FixtureSyncGame[] = fixtures.map(game => ({ ...game }))
	const skipIds = new Set(skipMatchIds)

	// Pre-pass: find which fixture entry each local game (including locked) pairs with.
	// We include locked games here so we can identify which fixture entries they correspond
	// to and exclude those entries from the main pass, preventing spurious "New Game Found".
	const allPairings = pairFixtureGames(workingGames, games)
	const lockedGameToMatchId = new Map([...allPairings].filter(([gid]) => games[gid].syncLock))
	const lockedMatchIds = new Set(lockedGameToMatchId.values())

	const filteredNewGames = workingGames.filter(g => !skipIds.has(g.id) && !lockedMatchIds.has(g.id))

	// Skipped fixture entries that are still in the current fixture, and therefore
	// relevant to show in sync management.
	const suppressedAdds: SuppressedAdd[] = workingGames
		.filter(g => skipIds.has(g.id))
		.map(g => ({
			matchId: g.id,
			game: {
				round: g.round,
				opponent: g.opponent,
				date: g.date,
				venue: g.venue,
				created: Date.now(),
				...(g.result ? { result: g.result } : {}),
			},
		}))

	// Main pass: pair unlocked games with filtered fixture entries.
	const mainPairings = pairFixtureGames(filteredNewGames, games, g => !!g.syncLock)
	for (const [gameId, matchId] of mainPairings) {
		const match = filteredNewGames.find(g => g.id === matchId)
		if (match) match.myGameId = gameId
	}

	// Locked games that are relevant to the current sync and would have been deleted or updated.
	// We intentionally ignore result-only changes here; locked games are still listed under
	// skipped games, and this keeps the management UI focused on fixture shape changes.
	const lockCheckFields = [ 'date', 'opponent', 'round', 'venue' ] as const
	const suppressedGames: SuppressedGame[] = Object.keys(games)
		.filter(gameId => games[gameId].syncLock)
		.reduce((acc: SuppressedGame[], gameId: string) => {
			const game = games[gameId]
			const matchedId = lockedGameToMatchId.get(gameId)
			const matchingEntry = matchedId != null ? workingGames.find(ng => ng.id === matchedId) : undefined
			if (!matchingEntry) {
				acc.push({ game, wouldDelete: true })
			} else if (lockCheckFields.some(f => matchingEntry[f] !== game[f])) {
				const wouldUpdateChanges: FieldChange[] = lockCheckFields
					.filter(f => matchingEntry[f] !== game[f])
					.map(f => ({
						field: f as keyof Game,
						oldValue: game[f],
						newValue: matchingEntry[f] as Game[keyof Game],
					}))
				acc.push({ game, wouldDelete: false, wouldUpdateChanges })
			}
			return acc
		}, [])

	const fields = [ 'date', 'opponent', 'round', 'venue', 'result' ] as const

	const updates: Array<ChangeAddGame | ChangeDeleteGame | {
		myGameId: GameId
		field: keyof Game
		oldValue: Game[keyof Game]
		newValue: Game[keyof Game]
	}> = [ ]

	filteredNewGames.forEach(game => {
		const { myGameId } = game
		if (!myGameId) {
			updates.push({
				add: true,
				game: {
					date: game.date,
					opponent: game.opponent,
					round: game.round,
					venue: game.venue,
					...(game.result ? { result: game.result } : {}),
					created: Date.now(),
				},
				matchId: game.id,
			})
			return
		}

		if (games[myGameId].syncLock) return

		fields.forEach(field => {
			let isDifferent = false
			if (field === 'result') {
				// Never suggest removing a score.
				if (game.result && areDifferentResults(games[myGameId].result, game.result)) {
					isDifferent = true
				}
			} else if (game[field] !== games[myGameId][field]) {
				isDifferent = true
			}
			if (isDifferent) {
				console.log("FOUND DIFFERENCE", myGameId, game[field], games[myGameId][field])
				updates.push({
					myGameId,
					field,
					oldValue: games[myGameId][field],
					newValue: game[field],
				})
			}
		})
	})

	Object.keys(games)
		.filter(gameId => !games[gameId].syncLock && !filteredNewGames.find(game => game.myGameId === gameId))
		.sort((a, b) => compareFixtureOrder(
			{ date: games[a].date, round: games[a].round, key: a },
			{ date: games[b].date, round: games[b].round, key: b },
		))
		.forEach(gameId => {
		updates.push({
			delete: gameId,
			game: games[gameId],
		})
	})

	console.log('UPDATES', updates)

	const changes: Changes = [ ]

	updates.forEach(change => {
		if ('add' in change || 'delete' in change) {
			changes.push(change)
		} else {
			const { myGameId, field, oldValue, newValue } = change

			const obj = {
				field,
				oldValue,
				newValue,
			}

			const last = changes.length && changes[changes.length - 1]
			if (last && 'myGameId' in last && last.myGameId === myGameId) {
				last.changes.push(obj)
			} else {
				changes.push({
					myGameId,
					changes: [ obj ],
				})
			}
		}
	})

	return { changes: sortFixtureChanges(changes, games), suppressedGames, suppressedAdds }
}

function isSameRound(r1: string | number, r2: string | number) {
	if (String(r1).toLowerCase() === String(r2).toLowerCase()) {
		return true
	}
	if (!String(r1).match(/final/i) && !String(r2).match(/final/i)) {
		const re = new RegExp(/\d+/g)
		const n1 = String(r1).match(re)?.[0]
		const n2 = String(r2).match(re)?.[0]
		if (n1 && n1 === n2) {
			return true
		}
	}
	return false
}

function areDifferentResults(result1: Result | undefined, result2: Result | undefined) {
	return result1?.me.length !== result2?.me.length || result1?.me.some((v, i) => v !== result2?.me[i]) || result1?.them.some((v, i) => v !== result2?.them[i])
}

function isRecentlyCompleted(gameDate: number) {
	return Date.now() - gameDate <= QUICK_PERIOD_SCORE_RECENT_WINDOW_MS
}

function hasFiniteScore(score: number | null | undefined): score is number {
	return typeof score === 'number' && Number.isFinite(score)
}

function periodScoreAttemptScoreKey(props: Pick<PeriodScoreAttemptProps, 'status' | 'meScore' | 'themScore'>) {
	return [
		String(props.status ?? 'unknown'),
		hasFiniteScore(props.meScore) ? String(props.meScore) : 'no-me-score',
		hasFiniteScore(props.themScore) ? String(props.themScore) : 'no-them-score',
	].join(':')
}

function loadPeriodScoreAttemptCache(): PeriodScoreAttemptCache {
	try {
		const raw = localStorage.getItem(PERIOD_SCORE_ATTEMPT_CACHE_KEY)
		const parsed = raw ? JSON.parse(raw) : null
		if (!parsed || typeof parsed !== 'object') {
			return {}
		}

		const cache: PeriodScoreAttemptCache = {}
		Object.entries(parsed as Record<string, unknown>).forEach(([key, value]) => {
			if (!value || typeof value !== 'object') {
				return
			}

			const entry = value as Record<string, unknown>
			if (
				typeof entry.checkedAt === 'number' &&
				typeof entry.scoreKey === 'string' &&
				typeof entry.hadPeriodScores === 'boolean'
			) {
				cache[key] = {
					checkedAt: entry.checkedAt,
					scoreKey: entry.scoreKey,
					hadPeriodScores: entry.hadPeriodScores,
				}
			}
		})
		return cache
	} catch (err) {
		console.warn('Unable to read fixture period score attempt cache.', err)
		return {}
	}
}

function savePeriodScoreAttemptCache(cache: PeriodScoreAttemptCache) {
	try {
		const prunedEntries = Object.entries(cache)
			.sort((a, b) => b[1].checkedAt - a[1].checkedAt)
			.slice(0, PERIOD_SCORE_ATTEMPT_CACHE_MAX_ENTRIES)
		localStorage.setItem(PERIOD_SCORE_ATTEMPT_CACHE_KEY, JSON.stringify(Object.fromEntries(prunedEntries)))
	} catch (err) {
		console.warn('Unable to save fixture period score attempt cache.', err)
	}
}
